import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Layout } from "@/components/layout/Layout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import IndexPage from "@/pages/IndexPage";
import LegalPage from "@/pages/LegalPage";
import CorporateInformation from "@/pages/CorporateInformation";
import Kyc from "@/pages/Kyc";
import AntiFraud from "@/pages/AntiFraud";
import { DetailPage } from "@/components/common/DetailPage";
import { LandingPage } from "@/components/landing/LandingPage";
import { imageFor } from "@/lib/imagery";
import { DEFAULT_LOCALE, LOCALES, type RouteKey } from "@/lib/routes";

// Peer pages passed to LandingPage as "Related" cards
const SERVICES: RouteKey[] = ["services.trade", "services.sourcing", "services.supply", "services.consulting"];
const COMMODITIES: RouteKey[] = ["commodities.grains", "commodities.food", "commodities.petrochemical"];
const COMPLIANCE_ITEMS: RouteKey[] = ["compliance.corporate", "compliance.kyc", "compliance.antifraud"];
const RESOURCES: RouteKey[] = ["resources.profile", "resources.trade-docs"];

function peers(all: RouteKey[], self: RouteKey): RouteKey[] {
  return all.filter((k) => k !== self);
}

// Image maps sourced from the central registry — no inline URLs.
function imageMapFor(keys: RouteKey[]): Partial<Record<RouteKey, string>> {
  const m: Partial<Record<RouteKey, string>> = {};
  keys.forEach((k) => {
    const img = imageFor(k);
    if (img) m[k] = img.url;
  });
  return m;
}

/** Guards the locale segment and mounts the whole locale-scoped tree. */
function LocaleGuard() {
  const { locale } = useParams();
  if (!locale || !(LOCALES as string[]).includes(locale)) {
    return <Navigate to={`/${DEFAULT_LOCALE}`} replace />;
  }
  return (
    <I18nProvider>
      <Layout />
    </I18nProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to={`/${DEFAULT_LOCALE}`} replace />} />

        <Route path="/:locale" element={<LocaleGuard />}>
          <Route index element={<Home />} />
          <Route path="about" element={<About />} />

          {/* Services — full landing pages */}
          <Route
            path="services"
            element={
              <IndexPage
                routeKey="services.index"
                items={SERVICES}
                imageMap={imageMapFor(SERVICES)}
                heroImageUrl={imageFor("services.index")?.url}
              />
            }
          />
          <Route path="services/international-trade"    element={<LandingPage routeKey="services.trade"      related={peers(SERVICES, "services.trade")} />} />
          <Route path="services/global-sourcing"        element={<LandingPage routeKey="services.sourcing"   related={peers(SERVICES, "services.sourcing")} />} />
          <Route path="services/commodity-supply"       element={<LandingPage routeKey="services.supply"     related={peers(SERVICES, "services.supply")} />} />
          <Route path="services/commercial-consulting"  element={<LandingPage routeKey="services.consulting" related={peers(SERVICES, "services.consulting")} />} />

          {/* Commodities — full landing pages */}
          <Route
            path="commodities"
            element={
              <IndexPage
                routeKey="commodities.index"
                items={COMMODITIES}
                imageMap={imageMapFor(COMMODITIES)}
                heroImageUrl={imageFor("commodities.index")?.url}
              />
            }
          />
          <Route path="commodities/grains-cereals"         element={<LandingPage routeKey="commodities.grains"        related={peers(COMMODITIES, "commodities.grains")} />} />
          <Route path="commodities/food-products"          element={<LandingPage routeKey="commodities.food"          related={peers(COMMODITIES, "commodities.food")} />} />
          <Route path="commodities/petrochemical-products" element={<LandingPage routeKey="commodities.petrochemical" related={peers(COMMODITIES, "commodities.petrochemical")} />} />

          {/* Compliance — placeholder-first detail pages */}
          <Route
            path="compliance"
            element={
              <IndexPage
                routeKey="compliance.index"
                items={COMPLIANCE_ITEMS}
                imageMap={imageMapFor(COMPLIANCE_ITEMS)}
                heroImageUrl={imageFor("compliance.index")?.url}
              />
            }
          />
          <Route path="compliance/corporate-information" element={<CorporateInformation />} />
          <Route path="compliance/kyc-verification"      element={<Kyc />} />
          <Route path="compliance/anti-fraud-notice"     element={<AntiFraud />} />

          {/* Resources — placeholder-first detail pages */}
          <Route
            path="resources"
            element={
              <IndexPage
                routeKey="resources.index"
                items={RESOURCES}
                imageMap={imageMapFor(RESOURCES)}
                heroImageUrl={imageFor("resources.index")?.url}
              />
            }
          />
          <Route path="resources/company-profile"  element={<DetailPage routeKey="resources.profile"     imageUrl={imageFor("resources.profile")?.url}     imageAlt={imageFor("resources.profile")?.alt}     related={peers(RESOURCES, "resources.profile")} />} />
          <Route path="resources/downloads"        element={<DetailPage routeKey="resources.downloads"   imageUrl={imageFor("resources.downloads")?.url}   imageAlt={imageFor("resources.downloads")?.alt}   related={peers(RESOURCES, "resources.downloads")} />} />
          <Route path="resources/trade-documents"  element={<DetailPage routeKey="resources.trade-docs"  imageUrl={imageFor("resources.trade-docs")?.url}  imageAlt={imageFor("resources.trade-docs")?.alt}  related={peers(RESOURCES, "resources.trade-docs")} />} />

          {/* Contact */}
          <Route path="contact" element={<Contact />} />

          {/* Legal */}
          <Route path="legal/privacy-policy"           element={<LegalPage routeKey="legal.privacy" />} />
          <Route path="legal/cookie-policy"            element={<LegalPage routeKey="legal.cookies" />} />
          <Route path="legal/terms-of-use"             element={<LegalPage routeKey="legal.terms" />} />
          <Route path="legal/accessibility-statement"  element={<LegalPage routeKey="legal.accessibility" />} />
          <Route path="legal/legal-notice"             element={<LegalPage routeKey="legal.notice" />} />

          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="*" element={<Navigate to={`/${DEFAULT_LOCALE}`} replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
